/**
 * C · Order tracking — submits email + order number to the CRM storefront API.
 * The real carrier tracking number/URL is never rendered here, even if the
 * API response includes it — this page only ever shows the 5 status steps.
 */

const FALLBACK_LABELS = {
  shipped: 'Order shipped',
  in_transit: 'In transit',
  arrived: 'Arrived at destination',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
};

function getStepLabels(root) {
  return {
    shipped: root.getAttribute('data-label-step-shipped') || FALLBACK_LABELS.shipped,
    in_transit: root.getAttribute('data-label-step-in-transit') || FALLBACK_LABELS.in_transit,
    arrived: root.getAttribute('data-label-step-arrived') || FALLBACK_LABELS.arrived,
    out_for_delivery:
      root.getAttribute('data-label-step-out-for-delivery') || FALLBACK_LABELS.out_for_delivery,
    delivered: root.getAttribute('data-label-step-delivered') || FALLBACK_LABELS.delivered,
  };
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderItems(root, items, orderId) {
  const labels = getStepLabels(root);
  const awaitingLabel = root.getAttribute('data-label-awaiting') || '';

  return (items || [])
    .map((item) => {
      const steps = item.steps || [];
      const anyReached = steps.some((step) => step.reached);
      // The step right after the last reached one is "current" — i.e. the
      // stage the shipment is actively in. -1 (no highlight) once every
      // step is reached, since there's nothing left to point at.
      const currentIndex = steps.findIndex((step) => !step.reached);

      const stepsHtml = steps
        .map((step, index, all) => {
          const done = step.reached ? ' is-done' : '';
          const current = index === currentIndex ? ' is-current' : '';
          const lineClass =
            index < all.length - 1 && all[index + 1]?.reached ? ' is-done' : '';
          const line =
            index === all.length - 1
              ? ''
              : `<span class="c-track__line${lineClass}"></span>`;
          // Label is always resolved from our own schema-driven copy —
          // never from anything the API sends about the carrier.
          const label = labels[step.key] || step.label;
          return `<li class="c-track__step${done}${current}"><span class="c-track__rail"><span class="c-track__dot"></span>${line}</span><span class="c-track__step-label">${escapeHtml(label)}</span></li>`;
        })
        .join('');

      // No carrier name, tracking number, or tracking link is ever rendered.
      // Only a generic pre-shipment message when nothing has happened yet.
      const status =
        !anyReached && awaitingLabel
          ? `<p class="c-track__carrier">${escapeHtml(awaitingLabel)}</p>`
          : '';

      return `<div class="c-track__item"><p class="c-track__item-name">${escapeHtml(item.name || orderId)}</p><ol class="c-track__steps">${stepsHtml}</ol>${status}</div>`;
    })
    .join('');
}

function initTracking(root) {
  if (!(root instanceof Element) || root.dataset.cTrackReady === 'true') return;
  const form = root.querySelector('[data-crm-track-form], form');
  const emailInput = root.querySelector('[data-crm-track-email]');
  const orderInput = root.querySelector('[data-crm-track-order]');
  const button = root.querySelector('[data-crm-track-submit]');
  const msg = root.querySelector('[data-crm-track-msg]');
  const result = root.querySelector('[data-crm-track-result]');
  if (!form || !emailInput || !orderInput || !button) return;

  root.dataset.cTrackReady = 'true';
  const endpoint = root.getAttribute('data-endpoint') || '';
  const shop = root.getAttribute('data-shop') || '';
  const token = root.getAttribute('data-token') || '';

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (msg) {
      msg.hidden = true;
      msg.textContent = '';
    }
    if (result) result.innerHTML = '';
    button.disabled = true;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          token,
          email: emailInput.value,
          orderId: orderInput.value,
        }),
      });
      const body = await response.json();
      if (response.status === 403 || !response.ok) throw new Error('lookup failed');
      if (!body.found) {
        if (msg) {
          msg.textContent = root.getAttribute('data-label-not-found') || '';
          msg.hidden = false;
        }
        return;
      }
      if (result) result.innerHTML = renderItems(root, body.items, body.orderId);
    } catch {
      if (msg) {
        msg.textContent = root.getAttribute('data-label-error') || '';
        msg.hidden = false;
      }
    } finally {
      button.disabled = false;
    }
  });
}

function initAll(scope = document) {
  const root = scope instanceof Element ? scope : document;
  root.querySelectorAll('[data-c-track]').forEach(initTracking);
}

initAll(document);
document.addEventListener('shopify:section:load', (event) => initAll(event.target));

export { initTracking };